import { useState } from "react";
import { cn } from "@cieda/djayma-ui";
import { Skeleton } from "@cieda/djayma-ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditEntry {
  readonly id: string;
  readonly action: string;
  readonly user: string;
  readonly timestamp: Date;
  readonly details?: Readonly<Record<string, unknown>>;
}

export interface AuditTimelineProps {
  readonly entries: readonly AuditEntry[];
  readonly isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Action color mapping
// ---------------------------------------------------------------------------

const ACTION_COLORS: Readonly<Record<string, string>> = {
  create: "bg-emerald-500",
  update: "bg-blue-500",
  delete: "bg-red-500",
  approve: "bg-green-500",
  reject: "bg-red-500",
  payment: "bg-amber-500",
  delivery: "bg-purple-500",
};

function getActionColor(action: string): string {
  // Match against known prefixes / keywords
  const lower = action.toLowerCase();
  for (const [key, color] of Object.entries(ACTION_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return "bg-stone-400";
}

// ---------------------------------------------------------------------------
// Single timeline entry
// ---------------------------------------------------------------------------

function TimelineEntry({ entry }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <div className="relative flex gap-3 pb-6 last:pb-0">
      {/* Vertical line */}
      <div className="absolute left-[7px] top-4 bottom-0 w-px bg-stone-200 last:hidden" />

      {/* Dot */}
      <div
        className={cn(
          "relative z-10 mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-white",
          getActionColor(entry.action),
        )}
      />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium text-stone-900">{entry.action}</span>
          <span className="text-xs text-stone-500">
            par {entry.user}
          </span>
        </div>

        <p className="mt-0.5 text-xs text-stone-400">
          {format(entry.timestamp, "dd MMM yyyy 'à' HH:mm", { locale: fr })}
        </p>

        {hasDetails && (
          <>
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
            >
              {expanded ? "Masquer les détails" : "Voir les détails"}
              {expanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>

            {expanded && (
              <div className="mt-2 rounded-md border border-stone-100 bg-stone-50 p-3">
                <pre className="overflow-x-auto text-xs text-stone-600">
                  {JSON.stringify(entry.details, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="h-3.5 w-3.5 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuditTimeline({ entries, isLoading }: AuditTimelineProps) {
  if (isLoading) return <TimelineSkeleton />;

  if (entries.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-stone-400">
        Aucune activité enregistrée.
      </p>
    );
  }

  return (
    <div className="space-y-0">
      {entries.map((entry) => (
        <TimelineEntry key={entry.id} entry={entry} />
      ))}
    </div>
  );
}
